import { useConfig } from "@jti/app-context/useConfig";
import { isDeliveryMethodAtHomeDelivery } from "@jti/commerce/shared";
import type { PaymentMethod } from "@jti/fe-common/types";
import { customPaymentMethods } from "@jti/fe-common/types";
import type { OrderItem, Product } from "@jti/magento";
import type { MagentoPaymentMethod } from "@jti/magento-types";
import { useTranslations } from "@jti/ui";

import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider/hooks/useCheckoutV2Configuration";
import { checkIfSubscriptionOrder } from "@checkout-v2/shared/utils/checkIfSubscriptionOrder";

import type { PaymentOption } from "../../components/PaymentMethodsSection";
import { hasRestrictedSkusForPayOnDelivery } from "../../utils/hasRestrictedSkusForPayOnDelivery";
import { isPayOnDeliveryEnabled } from "../../utils/isPayOnDeliveryEnabled";
import { mapMethodsToPaymentOptions } from "../../utils/mapMethodsToPaymentOption";
import { sortMethodsByAvailability } from "../../utils/sortPaymentMethodsByAvailability";

type PreparePaymentOptionsArgs = {
  evoMethods: PaymentMethod[] | undefined;
  magentoMethods: MagentoPaymentMethod[] | undefined;
  selectedShippingMethodCode: string | undefined;
  orderItems: Product[] | OrderItem[] | undefined;
  methodAndCarrierCode?: string;
};

export const usePreparedPaymentOptions = (args: PreparePaymentOptionsArgs): PaymentOption[] => {
  const { __ } = useTranslations();
  const config = useConfig();
  const checkoutConfiguration = useCheckoutV2Configuration();
  const isSubscription = checkIfSubscriptionOrder(args.orderItems);

  const { evoMethods, magentoMethods, selectedShippingMethodCode, orderItems = [], methodAndCarrierCode } = args;
  const showPayOnDelivery = isPayOnDeliveryEnabled(magentoMethods);

  const restrictedSkus = config.restrictedSkusForPayOnDelivery ?? "";
  const hasRestrictedItems = hasRestrictedSkusForPayOnDelivery(restrictedSkus, orderItems);
  const shouldDisablePayOnDelivery = hasRestrictedItems || isSubscription;

  const selectedShippingMethod = checkoutConfiguration.shippingMethods?.find(
    (method) => method.methodCode === selectedShippingMethodCode
  );

  const isAtHomeDelivery =
    selectedShippingMethod?.type === "atHome" || isDeliveryMethodAtHomeDelivery(methodAndCarrierCode);

  const finalMethods: PaymentMethod[] = (evoMethods || []).map((method) => ({
    ...method,
    title: __(`cart.order.payment_method.${method.code}`, undefined, method.title),
  }));

  if (showPayOnDelivery && (isAtHomeDelivery || config.allowPayInStoreOnDelivery)) {
    finalMethods.push({
      code: customPaymentMethods.payOnDelivery,
      title: __("cart.order.payment_method.pay_on_delivery"),
      definition: __("cart.order.payment_method.pay_on_delivery.definition"),
      enabled: !shouldDisablePayOnDelivery,
    });
  }

  const sortedMethods = sortMethodsByAvailability(finalMethods);

  return mapMethodsToPaymentOptions(sortedMethods);
};
